import { Logger as ConsoleLogger } from './impl/console';
import { Logger as PinoLogger } from './impl/pino';
import { Logger as LoggerInstance } from './logger';

// Export Logger type for external use
export type { Logger } from './logger';

export { SENSITIVE_FIELDS } from './redaction';

// Both implementations are imported statically (#3069), so nothing is ever
// left in flight. pino is a Next server external; a client graph that reaches
// this module gets `pino/browser.js` through pino's `browser` field, as it
// already did before this file resolved the logger synchronously.
const LOGGER = process.env.LOGGER ?? 'pino';

let warnedUnknownProvider = false;

/**
 * @name getLoggerSync
 * @description The configured logger, available at once.
 *
 * A logger that has to be awaited gets dropped: a service that stored it from
 * an un-awaited promise in its constructor skipped every line it logged before
 * its first real I/O wait, silently, even with a working logger (measured on
 * dev 2026-10-07: lines logged immediately, after one microtask and after
 * three were all lost). Assign this in a field initializer instead.
 *
 * An unknown `LOGGER` value logs to the console and says so once on
 * `console.error`, rather than leaving a service with no logger at all.
 */
export function getLoggerSync(): LoggerInstance {
  switch (LOGGER) {
    case 'pino':
      return PinoLogger;
    case 'console':
      return ConsoleLogger;
    default:
      if (!warnedUnknownProvider) {
        warnedUnknownProvider = true;
        console.error(
          `[@kit/logger] LOGGER="${LOGGER}" is not a logger (expected pino or console); logging to the console instead`,
        );
      }
      return ConsoleLogger;
  }
}

/**
 * @name getLogger
 * @description The configured logger, as a promise. Kept for its existing
 * callers; new code that cannot await should use `getLoggerSync`.
 */
export async function getLogger(): Promise<LoggerInstance> {
  return getLoggerSync();
}

let warnedCallbackFailure = false;

/**
 * @name withLogger
 * @description Run `write` with the logger now, and never throw.
 *
 * For logging from a `catch` block or other code that must not fail because
 * a log call did. A throw from `write`, or a rejection of the promise it
 * returns, is reported once per process on
 * `console.warn` (the logger may be what failed) and is otherwise dropped.
 */
export function withLogger(write: (logger: LoggerInstance) => unknown): void {
  try {
    const result = write(getLoggerSync());
    // An async callback fails by rejecting, not by throwing.
    if (isPromiseLike(result)) {
      Promise.resolve(result).catch(reportCallbackFailure);
    }
  } catch (error: unknown) {
    reportCallbackFailure(error);
  }
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

function reportCallbackFailure(error: unknown): void {
  if (warnedCallbackFailure) return;
  warnedCallbackFailure = true;
  console.warn(
    '[@kit/logger] a log call failed; later failures are silent',
    error,
  );
}
